/**
 * Collects voucher UUIDs from svc-payment `response_json` (promo and voucher payment shapes vary by version).
 */

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

function collectVoucherIdsFromLogs(ids: Set<string>, voucherLogs: unknown): void {
  const add = (id: unknown) => {
    if (typeof id === 'string' && id.length > 0) {
      ids.add(id);
    }
  };

  if (!Array.isArray(voucherLogs)) {
    return;
  }
  for (const log of voucherLogs) {
    if (log && typeof log === 'object') {
      add((log as { voucherId?: string }).voucherId);
    }
  }
}

export function getVoucherIdsFromPromoPaymentResponse(
  responseJson: Record<string, unknown> | null | undefined,
): Array<string> {
  if (!responseJson || typeof responseJson !== 'object') {
    return [];
  }

  const ids = new Set<string>();

  collectVoucherIdsFromLogs(ids, responseJson.voucherLogs);
  const nestedDetails = asRecord(responseJson.details);
  if (nestedDetails) {
    collectVoucherIdsFromLogs(ids, nestedDetails.voucherLogs);
  }

  const topVouchersApplied = responseJson.vouchersApplied;
  if (Array.isArray(topVouchersApplied)) {
    for (const v of topVouchersApplied) {
      if (v && typeof v === 'object') {
        const id = (v as { voucherId?: string }).voucherId;
        if (typeof id === 'string' && id.length > 0) {
          ids.add(id);
        }
      }
    }
  }

  return Array.from(ids);
}

/** First voucher UUID from `response_json`, if any. */
export function getFirstVoucherIdFromPromoPaymentResponse(
  responseJson: Record<string, unknown> | null | undefined,
): string | undefined {
  return getVoucherIdsFromPromoPaymentResponse(responseJson)[0];
}

/** UI-oriented fields extracted from svc-payment voucher `response_json` (optional `details` wrapper). */
export type VoucherPaymentResponseDetails = {
  voucherName?: string;
  rewardKind?: string;
  creditAmount?: number;
};

/**
 * Reads voucher log / reward metadata from payment response JSON. When `details` is present,
 * prefers `details.voucherLogs` and `details.rewardKind`, with top-level fallbacks.
 */
export function getVoucherPaymentResponseDetails(
  responseJson: Record<string, unknown> | null | undefined,
): VoucherPaymentResponseDetails {
  if (!responseJson || typeof responseJson !== 'object') {
    return {};
  }

  const nestedDetails = asRecord(responseJson.details);
  const voucherLogsRaw = nestedDetails?.voucherLogs ?? responseJson.voucherLogs;
  const logs = Array.isArray(voucherLogsRaw) ? voucherLogsRaw : [];
  const firstLog = logs[0] && typeof logs[0] === 'object' ? (logs[0] as Record<string, unknown>) : null;
  const metadata = firstLog ? asRecord(firstLog.metadata) : null;

  const nameRaw =
    metadata?.voucherName ??
    metadata?.voucherTitle ??
    (typeof metadata?.['voucher_name'] === 'string' ? metadata['voucher_name'] : undefined) ??
    (typeof metadata?.['voucher_title'] === 'string' ? metadata['voucher_title'] : undefined);
  const voucherName = typeof nameRaw === 'string' && nameRaw.trim() !== '' ? nameRaw.trim() : undefined;

  const rewardKindRaw =
    nestedDetails?.rewardKind ?? nestedDetails?.reward_kind ?? responseJson.rewardKind ?? responseJson.reward_kind;
  const rewardKind =
    typeof rewardKindRaw === 'string' && rewardKindRaw.trim() !== '' ? rewardKindRaw.trim() : undefined;

  const creditRaw = metadata?.creditAmount ?? metadata?.credit_amount;
  let creditAmount: number | undefined;
  if (typeof creditRaw === 'number' && !Number.isNaN(creditRaw)) {
    creditAmount = creditRaw;
  } else if (typeof creditRaw === 'string' && creditRaw.trim() !== '') {
    const parsed = Number.parseFloat(creditRaw);
    creditAmount = Number.isNaN(parsed) ? undefined : parsed;
  }

  return { voucherName, rewardKind, creditAmount };
}
